import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { useNavigate, useSearchParams, useLocation } from 'react-router-dom';
import {
  UserPlus,
  Users,
  Settings,
  Filter,
  Search,
  RefreshCw,
  CheckCircle,
  AlertCircle,
  Clock,
  MapPin,
  Target,
  BarChart3,
  Plus,
  Edit,
  Trash2,
  Save,
  Download,
  X,
  ChevronDown,
  Eye,
  ArrowUp,
  ArrowDown,
  ArrowUpDown
} from 'lucide-react';
import { API_ENDPOINTS } from '../../../api/config';
import Button from '../../../components/common/Button/Button';
import {
  sanitizeTextInput,
  sanitizeSearchInput,
  validateNumberInput,
  handleTextInputChange,
  handleSearchInputChange,
  handleNumberInputChange,
  getCharacterCount,
  FIELD_LIMITS,
  NUMBER_LIMITS
} from '../../../utils/inputValidation';
import assignmentRuleService from '../../../api/crm/assignment-rules/assignmentRuleService';
import leadService from '../../../api/crm/leads/leadService';
import { API_BASE_URL } from '../../../api/config';
import { toast } from '../../../utils/toast';
import { useAuth } from '../../../context/AuthContext';
import { useLeadStatuses } from '../../../hooks/useLeadQueries';
import { USER_ROLES } from '../../../utils/constants';
import PermissionGate from '../../../components/common/PermissionGate';
import { usePermissions } from '../../../hooks/usePermissions';
import { formatIndianAmount } from '../../../utils/amountFormatter';
import AssignmentRuleModal from './AssignmentRuleModal';
import ExportModal from '../../../components/common/ExportModal';
import { exportToCSV, generateExportFilename, downloadFromBackend } from '../../../utils/exportUtils';
import { formatDate } from '../../../utils/stringUtils';
import DateRangeFilter from '../../../components/common/DateRangeFilter';
import useUrlTableState from '../../../hooks/useUrlTableState';
import PaginationControls from '../../../components/common/Pagination/PaginationControls';

const STABLE_EMPTY_ARRAY = [];

const LeadAssignment = ({ onViewLead }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const { user } = useAuth();
  const { hasPermission } = usePermissions();

  const { data: masterLeadStatuses = STABLE_EMPTY_ARRAY } = useLeadStatuses();
  const masterLeadStatusesRef = useRef(STABLE_EMPTY_ARRAY);
  useEffect(() => {
    masterLeadStatusesRef.current = masterLeadStatuses;
  }, [masterLeadStatuses]);

  // Check if user has permission to create, edit, or delete (only super admin and sales manager)
  const canModify = user?.role === USER_ROLES.SUPER_ADMIN || user?.role === USER_ROLES.SALES_MANAGER;

  // Permission-based access control - use granular permissions instead of role checks
  // This allows custom permission assignments beyond just roles
  const canManageRules = hasPermission('lead_management', 'create') || hasPermission('lead_management', 'update');

  const handleViewLead = (leadId) => {
    if (onViewLead) {
      onViewLead(leadId);
    } else {
      navigate(`/lead_detail_360/${leadId}`, { state: { from: location.pathname + location.search } });
    }
  };
  const [searchParams, setSearchParams] = useSearchParams();
  const activeTab = searchParams.get('subTab') || 'unassigned';

  const setActiveTab = useCallback((tabKey) => {
    setSearchParams(prev => {
      const next = new URLSearchParams(prev);
      next.set('subTab', tabKey);
      return next;
    });
  }, [setSearchParams]);

  // Redirect to unassigned tab if user tries to access rules tab without permission
  useEffect(() => {
    if (activeTab === 'rules' && !canManageRules) {
      setActiveTab('unassigned');
    }
  }, [activeTab, canManageRules, setActiveTab]);

  // Clear selection when changing tabs
  useEffect(() => {
    setSelectedRows(new Set());
  }, [activeTab]);
  const [showRuleModal, setShowRuleModal] = useState(false);
  const [selectedRule, setSelectedRule] = useState(null);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [ruleToDelete, setRuleToDelete] = useState(null);
  const [isDeleting, setIsDeleting] = useState(false);
  const [users, setUsers] = useState([]);
  const [filteredUsers, setFilteredUsers] = useState([]); // Search results for modal dropdown
  const [loadingUsers, setLoadingUsers] = useState(false);

  // New states for export and selection
  const [selectedRows, setSelectedRows] = useState(new Set());
  const [showExportModal, setShowExportModal] = useState(false);
  const [exportLoading, setExportLoading] = useState(false);
  const [showBulkDeleteModal, setShowBulkDeleteModal] = useState(false);
  const [isBulkDeleting, setIsBulkDeleting] = useState(false);

  // Bulk assign state
  const [showBulkAssignModal, setShowBulkAssignModal] = useState(false);
  const [bulkAssignee, setBulkAssignee] = useState(null);
  const [bulkAssignUsers, setBulkAssignUsers] = useState([]);
  const [bulkAssignSearchTerm, setBulkAssignSearchTerm] = useState('');
  const [isAssigningBulk, setIsAssigningBulk] = useState(false);

  // Memoized check for selection in current tab
  const atLeastOneUnassignedSelected = useMemo(() => {
    if (activeTab !== 'unassigned') return false;
    return selectedRows.size > 0;
  }, [selectedRows, activeTab]);


  // State for rules and leads data
  const [assignmentRules, setAssignmentRules] = useState([]);
  const [loading, setLoading] = useState(false);
  const [unassignedLeads, setUnassignedLeads] = useState([]);
  const [loadingLeads, setLoadingLeads] = useState(false);
  const [unassignedPagination, setUnassignedPagination] = useState({
    currentPage: 1,
    totalPages: 1,
    totalLeads: 0,
    hasNextPage: false,
    hasPrevPage: false,
    limit: 10
  });
  const [totalUnassignedCount, setTotalUnassignedCount] = useState(0);
  const defaultUnassignedFilters = useMemo(() => ({
    page: 1,
    limit: 10,
    search: '',
    status: 'all',
    startDate: '',
    endDate: '',
    sortBy: 'createdAt',
    sortOrder: 'desc'
  }), []);

  const {
    filters: unassignedFilters,
    searchTerm: searchQuery,
    setSearchTerm: setSearchQuery,
    updateFilter: updateUnassignedFilter,
    updatePage: updateUnassignedPage,
    updateLimit: updateUnassignedLimit,
    updateSort: updateUnassignedSort,
  } = useUrlTableState(defaultUnassignedFilters);

  const defaultRuleFilters = useMemo(() => ({
    ruleSearch: '',
    ruleStatus: 'all',
    priority: 'all',
    assignmentType: 'all',
    ruleStartDate: '',
    ruleEndDate: '',
    ruleSortBy: 'name',
    ruleSortOrder: 'asc'
  }), []);

  const {
    filters: ruleFilters,
    searchTerm: ruleSearchQuery,
    setSearchTerm: setRuleSearchQuery,
    updateFilter: updateRuleFilter,
    updateSort: updateRuleSort,
  } = useUrlTableState(defaultRuleFilters, { searchKey: 'ruleSearch' });

  const currentPage = unassignedFilters.page;
  const itemsPerPage = unassignedFilters.limit;
  const debouncedSearchQuery = unassignedFilters.search || '';
  const statusFilter = unassignedFilters.status;

  const debouncedRuleSearchQuery = ruleFilters.ruleSearch || '';
  const ruleStatusFilter = ruleFilters.ruleStatus;
  const rulePriorityFilter = ruleFilters.priority;
  const ruleAssignmentTypeFilter = ruleFilters.assignmentType;

  const unassignedDateRange = useMemo(() => ({
    startDate: unassignedFilters.startDate || '',
    endDate: unassignedFilters.endDate || ''
  }), [unassignedFilters.startDate, unassignedFilters.endDate]);

  const rulesDateRange = useMemo(() => ({
    startDate: ruleFilters.ruleStartDate || '',
    endDate: ruleFilters.ruleEndDate || ''
  }), [ruleFilters.ruleStartDate, ruleFilters.ruleEndDate]);

  const unassignedSortColumn = unassignedFilters.sortBy;
  const unassignedSortOrder = unassignedFilters.sortOrder;
  const rulesSortColumn = ruleFilters.ruleSortBy;
  const rulesSortOrder = ruleFilters.ruleSortOrder;

  const setStatusFilter = useCallback((val) => updateUnassignedFilter({ status: val }), [updateUnassignedFilter]);
  const setUnassignedDateRange = useCallback((range) => updateUnassignedFilter({ startDate: range.startDate, endDate: range.endDate }), [updateUnassignedFilter]);
  const setCurrentPage = useCallback((page) => updateUnassignedPage(page), [updateUnassignedPage]);
  const setItemsPerPage = useCallback((limit) => updateUnassignedLimit(limit), [updateUnassignedLimit]);

  const setRuleStatusFilter = useCallback((val) => updateRuleFilter({ ruleStatus: val }), [updateRuleFilter]);
  const setRulePriorityFilter = useCallback((val) => updateRuleFilter({ priority: val }), [updateRuleFilter]);
  const setRuleAssignmentTypeFilter = useCallback((val) => updateRuleFilter({ assignmentType: val }), [updateRuleFilter]);
  const setRulesDateRange = useCallback((range) => updateRuleFilter({ ruleStartDate: range.startDate, ruleEndDate: range.endDate }), [updateRuleFilter]);

  const handleRulesSort = useCallback((column) => {
    if (rulesSortColumn === column) {
      updateRuleSort(column, rulesSortOrder === 'asc' ? 'desc' : 'asc');
    } else {
      updateRuleSort(column, 'asc');
    }
  }, [rulesSortColumn, rulesSortOrder, updateRuleSort]);

  const handleUnassignedSort = useCallback((column) => {
    if (unassignedSortColumn === column) {
      updateUnassignedSort(column, unassignedSortOrder === 'asc' ? 'desc' : 'asc');
    } else {
      updateUnassignedSort(column, 'asc');
    }
  }, [unassignedSortColumn, unassignedSortOrder, updateUnassignedSort]);

  // For unassigned leads table dropdown
  const [openDropdownLeadId, setOpenDropdownLeadId] = useState(null);
  const [leadSearchTerms, setLeadSearchTerms] = useState({});
  const [leadFilteredUsers, setLeadFilteredUsers] = useState({});
  const [leadAllUsers, setLeadAllUsers] = useState({});
  const [dropdownPositions, setDropdownPositions] = useState({});
  const leadDropdownRefs = useRef({});

  // Auto assignment state
  const [isRunningAutoAssignment, setIsRunningAutoAssignment] = useState(false);
  const [showAutoAssignmentModal, setShowAutoAssignmentModal] = useState(false);
  const [notification, setNotification] = useState(null);

  const getSortedUnassignedLeads = () => {
    if (!unassignedSortColumn || !unassignedLeads || unassignedLeads.length === 0) return unassignedLeads;

    return [...unassignedLeads].sort((a, b) => {
      let aValue, bValue;

      if (unassignedSortColumn === 'leadId') {
        aValue = a.leadId || '';
        bValue = b.leadId || '';
      } else if (unassignedSortColumn === 'details') {
        aValue = getLeadName(a).toLowerCase();
        bValue = getLeadName(b).toLowerCase();
      } else if (unassignedSortColumn === 'score') {
        aValue = Number(a.score) || 0;
        bValue = Number(b.score) || 0;
      } else if (unassignedSortColumn === 'status') {
        aValue = mapStageToFrontendStatus(a.stage || a.status).toLowerCase();
        bValue = mapStageToFrontendStatus(b.stage || b.status).toLowerCase();
      } else if (unassignedSortColumn === 'projectCost') {
        aValue = Number(a.totalCost) || 0;
        bValue = Number(b.totalCost) || 0;
      } else if (unassignedSortColumn === 'updatedAt') {
        aValue = new Date(a.updatedAt || a.createdAt || 0).getTime();
        bValue = new Date(b.updatedAt || b.createdAt || 0).getTime();
      } else if (unassignedSortColumn === 'createdAt') {
        aValue = new Date(a.createdAt || 0).getTime();
        bValue = new Date(b.createdAt || 0).getTime();
      } else {
        aValue = a[unassignedSortColumn] || '';
        bValue = b[unassignedSortColumn] || '';
      }

      if (typeof aValue === 'string') aValue = aValue.toLowerCase();
      if (typeof bValue === 'string') bValue = bValue.toLowerCase();

      if (aValue < bValue) return unassignedSortOrder === 'asc' ? -1 : 1;
      if (aValue > bValue) return unassignedSortOrder === 'asc' ? 1 : -1;
      return 0;
    });
  };

  const getSortedAssignmentRules = () => {
    if (!rulesSortColumn || !assignmentRules || assignmentRules.length === 0) return assignmentRules;

    return [...assignmentRules].sort((a, b) => {
      let aValue, bValue;

      if (rulesSortColumn === 'name') {
        aValue = (a.name || '').toLowerCase();
        bValue = (b.name || '').toLowerCase();
      } else if (rulesSortColumn === 'isActive') {
        aValue = a.isActive ? 1 : 0;
        bValue = b.isActive ? 1 : 0;
      } else if (rulesSortColumn === 'priority') {
        aValue = Number(a.priority) || 0;
        bValue = Number(b.priority) || 0;
      } else if (rulesSortColumn === 'assignees') {
        aValue = (a.assignees || []).length;
        bValue = (b.assignees || []).length;
      } else if (rulesSortColumn === 'assignmentType') {
        aValue = (a.assignmentType || '').toLowerCase();
        bValue = (b.assignmentType || '').toLowerCase();
      } else if (rulesSortColumn === 'createdAt') {
        aValue = new Date(a.createdAt || 0).getTime();
        bValue = new Date(b.createdAt || 0).getTime();
      } else if (rulesSortColumn === 'updatedAt') {
        aValue = new Date(a.updatedAt || a.createdAt || 0).getTime();
        bValue = new Date(b.updatedAt || b.createdAt || 0).getTime();
      } else {
        aValue = a[rulesSortColumn] || '';
        bValue = b[rulesSortColumn] || '';
      }

      if (typeof aValue === 'string') aValue = aValue.toLowerCase();
      if (typeof bValue === 'string') bValue = bValue.toLowerCase();

      if (aValue < bValue) return rulesSortOrder === 'asc' ? -1 : 1;
      if (aValue > bValue) return rulesSortOrder === 'asc' ? 1 : -1;
      return 0;
    });
  };
  // Load user data for specific assignee IDs (used when editing rules)
  const loadUsersByIds = useCallback(async (assigneeIds) => {
    if (!assigneeIds || assigneeIds.length === 0) return;

    try {
      // Fetch users by searching for each ID or fetching all and filtering
      // Since backend doesn't support fetching by IDs directly, we'll fetch all and filter
      const response = await fetch(`${API_BASE_URL}/training-assignments/users?limit=100`, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('auth_token')}`,
          'Content-Type': 'application/json'
        }
      });

      const result = await response.json();

      if (result.responseCode === 200) {
        const usersData = result.data?.users || [];

        // Filter to only include assignees
        const assigneeUsers = usersData.filter(user => assigneeIds.includes(user._id));

        const transformedUsers = assigneeUsers.map(user => ({
          id: user._id,
          name: `${user.firstName} ${user.lastName}`.trim(),
          email: user.email,
          department: user.department,
          role: user.role
        }));

        // Add these users to the users array
        setUsers(prev => {
          const existingMap = new Map(prev.map(u => [u.id, u]));
          transformedUsers.forEach(u => existingMap.set(u.id, u));
          return Array.from(existingMap.values());
        });

        return transformedUsers;
      }
    } catch (error) {

    }
  }, []);

  // Fetch assignment rules
  const fetchRules = useCallback(async () => {
    setLoading(true);
    try {
      const filters = {
        search: debouncedRuleSearchQuery,
        status: ruleStatusFilter,
        priority: rulePriorityFilter,
        assignmentType: ruleAssignmentTypeFilter,
        startDate: rulesDateRange.startDate,
        endDate: rulesDateRange.endDate
      };

      const result = await assignmentRuleService.getAllRules(filters);
      if (result.success) {
        let rules = result.rules || [];

        setAssignmentRules(rules);

        // Extract all unique assignee IDs from all rules
        const allAssigneeIds = new Set();
        rules.forEach(rule => {
          rule.assignees.forEach(a => {
            const assigneeId = typeof a === 'object' ? (a._id || a.id) : a;
            if (assigneeId) {
              allAssigneeIds.add(assigneeId);
            }
          });
        });

        // Extract user data from populated assignees and add to users array
        const assigneeUserData = [];
        rules.forEach(rule => {
          rule.assignees.forEach(a => {
            if (typeof a === 'object' && (a._id || a.id)) {
              const assigneeId = a._id || a.id;
              if (!assigneeUserData.find(u => u.id === assigneeId)) {
                assigneeUserData.push({
                  id: assigneeId,
                  name: a.name || `${a.firstName || ''} ${a.lastName || ''}`.trim() || a.email || assigneeId,
                  email: a.email || '',
                  department: a.department || '',
                  role: a.role || ''
                });
              }
            }
          });
        });

        // Add assignee user data to users array
        if (assigneeUserData.length > 0) {
          setUsers(prev => {
            const existingMap = new Map(prev.map(u => [u.id, u]));
            assigneeUserData.forEach(u => existingMap.set(u.id, u));
            return Array.from(existingMap.values());
          });
        }

        // If some assignees are not populated (IDs only), fetch them
        const unpopulatedIds = Array.from(allAssigneeIds).filter(id =>
          !assigneeUserData.find(u => u.id === id)
        );
        if (unpopulatedIds.length > 0) {
          await loadUsersByIds(unpopulatedIds);
        }
      } else {
        toast.error(result.error || 'Failed to fetch assignment rules');
      }
    } catch (error) {

      toast.error('Failed to fetch assignment rules');
    } finally {
      setLoading(false);
    }
  }, [debouncedRuleSearchQuery, ruleStatusFilter, rulePriorityFilter, ruleAssignmentTypeFilter, rulesDateRange, loadUsersByIds]);

  // Handle items per page change for unassigned leads
  const handleUnassignedItemsPerPageChange = (limit) => {
    setItemsPerPage(limit);
    setCurrentPage(1);
  };

  // Map frontend status to backend stage
  const mapStatusToBackend = (status) => {
    const statusMap = {};
    const currentStatuses = masterLeadStatusesRef.current || [];
    currentStatuses.forEach(s => {
      statusMap[s.name] = s.value;
      statusMap[s.value] = s.value;
    });
    return statusMap[status] || status?.toLowerCase() || 'new';
  };

  // Map backend stage to frontend status display
  const mapStageToFrontendStatus = (stage) => {
    const normalizedStage = (stage || 'new').toLowerCase();
    const currentStatuses = masterLeadStatusesRef.current || [];
    const masterStatus = currentStatuses.find(s => s.value === normalizedStage || s.name.toLowerCase() === normalizedStage);
    if (masterStatus) return masterStatus.name;

    return stage || 'New';
  };

  // Fetch unassigned leads with pagination, search, and status filter
  const fetchUnassignedLeads = useCallback(async () => {
    // If status filter is active, wait for master statuses to load to map it properly
    if (statusFilter !== 'all' && masterLeadStatusesRef.current.length === 0) {
      return;
    }

    setLoadingLeads(true);
    try {
      // Build filters for backend
      const filters = {
        assignedTo: 'unassigned',
        page: currentPage,
        limit: itemsPerPage
      };

      // Add search filter if provided
      if (debouncedSearchQuery && debouncedSearchQuery.trim()) {
        filters.search = debouncedSearchQuery.trim();
      }

      // Add status filter if not 'all'
      if (statusFilter && statusFilter !== 'all') {
        filters.stage = mapStatusToBackend(statusFilter);
      }

      // Add date ranges
      if (unassignedDateRange.startDate) filters.startDate = unassignedDateRange.startDate;
      if (unassignedDateRange.endDate) filters.endDate = unassignedDateRange.endDate;

      const result = await leadService.getAllLeads(filters);
      if (result.success) {
        setUnassignedLeads(result.leads || []);
        // Update pagination state from API response
        if (result.pagination) {
          setUnassignedPagination({
            currentPage: result.pagination.currentPage || currentPage,
            totalPages: result.pagination.totalPages || 1,
            totalLeads: result.pagination.totalLeads || 0,
            hasNextPage: result.pagination.hasNextPage || false,
            hasPrevPage: result.pagination.hasPrevPage || false,
            limit: result.pagination.limit || itemsPerPage
          });
        }

        // Update total unassigned count from backend specific key if available
        if (result.unassignedCount !== undefined) {
          setTotalUnassignedCount(result.unassignedCount);
        } else {
          // Fallback: Check if we have active filters using localized logic to avoid scope issues
          const hasFilters = debouncedSearchQuery.trim() !== '' || statusFilter !== 'all';
          if (!hasFilters) {
            setTotalUnassignedCount(result.pagination?.totalLeads || 0);
          }
        }
      } else {
        toast.error(result.error || 'Failed to fetch unassigned leads');
      }
    } catch (error) {

      toast.error('Failed to fetch unassigned leads');
    } finally {
      setLoadingLeads(false);
    }
  }, [
    currentPage,
    itemsPerPage,
    debouncedSearchQuery,
    statusFilter,
    unassignedDateRange,
    statusFilter !== 'all' && masterLeadStatuses.length === 0
  ]);

  // Fetch CRM users for assignee selection (supports backend search)
  const loadUsers = useCallback(
    async (search = '', forLeadId = null) => {
      setLoadingUsers(true);
      try {
        const trimmedSearch = (search || '').trim();

        // Build query params for backend search + sensible limits
        const params = new URLSearchParams();
        if (trimmedSearch) {
          params.append('search', trimmedSearch);
        }
        // Use a smaller limit for per-lead dropdowns
        params.append('limit', forLeadId ? '20' : '50');
        // Only fetch sales team members (Sales Manager + Sales Executive) for lead assignment
        params.append('roleFilter', 'sales');

        const url = `${API_ENDPOINTS.CRM.TEAM_MANAGEMENT.USERS}${params.toString() ? `?${params.toString()}` : ''}`;

        // Use CRM-specific endpoint for team members with server-side search
        const response = await fetch(url, {
          method: 'GET',
          headers: {
            Authorization: `Bearer ${localStorage.getItem('auth_token')}`,
            'Content-Type': 'application/json'
          }
        });

        const result = await response.json();

        if (result.responseCode === 200) {
          const crmUsers = result.data?.users || [];

          // Transform users to match our format
          const transformedUsers = crmUsers
            .map(user => ({
              id: user.user || user._id || user.id,
              name: user.name || 'Unknown',
              email: user.email || '',
              department: user.department || '',
              role: user.role || '',
              designation: user.designation || ''
            }))
            .sort((a, b) => a.name.localeCompare(b.name));

          if (forLeadId === 'bulk') {
            setBulkAssignUsers(transformedUsers);
            return transformedUsers;
          }

          // If this is for a specific lead dropdown
          if (forLeadId) {
            // Store all users for this lead (already filtered by backend search)
            setLeadAllUsers(prev => ({
              ...prev,
              [forLeadId]: transformedUsers
            }));

            const limitedUsers = transformedUsers.slice(0, 10);
            setLeadFilteredUsers(prev => ({
              ...prev,
              [forLeadId]: limitedUsers
            }));
            return limitedUsers;
          }

          // Store all users (merge with existing to keep selected ones available)
          setUsers(prev => {
            const existingMap = new Map(prev.map(u => [u.id, u]));
            transformedUsers.forEach(u => existingMap.set(u.id, u));
            return Array.from(existingMap.values());
          });

          // Set filtered users for the dropdown (search results)
          setFilteredUsers(transformedUsers);
          return transformedUsers;
        }

        if (!forLeadId) setFilteredUsers([]);

        if (forLeadId) {
          setLeadFilteredUsers(prev => ({
            ...prev,
            [forLeadId]: []
          }));
          setLeadAllUsers(prev => ({
            ...prev,
            [forLeadId]: []
          }));
        }
        return [];
      } catch (error) {

        if (!forLeadId) setFilteredUsers([]);

        if (forLeadId) {
          setLeadFilteredUsers(prev => ({
            ...prev,
            [forLeadId]: []
          }));
          setLeadAllUsers(prev => ({
            ...prev,
            [forLeadId]: []
          }));
        }
        return [];
      } finally {
        setLoadingUsers(false);
      }
    },
    []
  );

  // Load users for a specific lead dropdown
  const loadUsersForLead = useCallback(async (leadId, searchTerm = '') => {
    await loadUsers(searchTerm, leadId);
  }, [loadUsers]);

  // Debounced backend search for per-lead assignee dropdowns
  useEffect(() => {
    if (!openDropdownLeadId) return;

    const searchTerm = leadSearchTerms[openDropdownLeadId] || '';
    const handler = setTimeout(() => {
      loadUsersForLead(openDropdownLeadId, searchTerm);
    }, 400);

    return () => clearTimeout(handler);
  }, [openDropdownLeadId, leadSearchTerms, loadUsersForLead]);

  // Calculate dropdown position when it opens
  useEffect(() => {
    if (openDropdownLeadId) {
      const ref = leadDropdownRefs.current[openDropdownLeadId];
      if (ref) {
        const rect = ref.getBoundingClientRect();
        setDropdownPositions(prev => ({
          ...prev,
          [openDropdownLeadId]: {
            top: rect.bottom + window.scrollY + 8,
            left: rect.left + window.scrollX,
            width: rect.width
          }
        }));
      }
    }
  }, [openDropdownLeadId]);

  // Handle click outside to close lead dropdowns
  useEffect(() => {
    const handleClickOutside = (event) => {
      Object.keys(leadDropdownRefs.current).forEach(leadId => {
        const ref = leadDropdownRefs.current[leadId];
        if (ref && !ref.contains(event.target)) {
          // Check if click is outside the dropdown itself
          const dropdownElement = document.querySelector(`[data-dropdown-lead-id="${leadId}"]`);
          if (dropdownElement && !dropdownElement.contains(event.target)) {
            if (openDropdownLeadId === leadId) {
              setOpenDropdownLeadId(null);
              setLeadSearchTerms(prev => ({
                ...prev,
                [leadId]: ''
              }));
            }
          } else if (!dropdownElement && openDropdownLeadId === leadId) {
            setOpenDropdownLeadId(null);
            setLeadSearchTerms(prev => ({
              ...prev,
              [leadId]: ''
            }));
          }
        }
      });
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [openDropdownLeadId]);



  // Load data on mount and when pagination/filters change
  useEffect(() => {
    fetchRules();
  }, [fetchRules]);

  useEffect(() => {
    fetchUnassignedLeads();
  }, [fetchUnassignedLeads]);

  useEffect(() => {
    loadUsers('');
  }, [loadUsers]);

  // Check if there are active filters
  const hasActiveFilters = useMemo(() => {
    return (
      searchQuery.trim() !== '' ||
      statusFilter !== 'all' ||
      unassignedDateRange.startDate !== '' ||
      unassignedDateRange.endDate !== ''
    );
  }, [searchQuery, statusFilter, unassignedDateRange]);

  // Clear all filters and search
  const handleClearFilters = () => {
    if (activeTab === 'unassigned') {
      updateUnassignedFilter({
        search: '',
        status: 'all',
        startDate: '',
        endDate: ''
      });
    } else {
      updateRuleFilter({
        ruleSearch: '',
        ruleStatus: 'all',
        priority: 'all',
        assignmentType: 'all',
        ruleStartDate: '',
        ruleEndDate: ''
      });
    }
  };

  // Helper function to get lead name
  const getLeadName = (lead) => {
    if (lead.firstName && lead.lastName) {
      return `${lead.firstName} ${lead.lastName}`;
    } else if (lead.firstName) {
      return lead.firstName;
    } else if (lead.lastName) {
      return lead.lastName;
    } else if (lead.name) {
      return lead.name;
    } else {
      return 'Unknown Lead';
    }
  };

  // Helper function to format date


  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    try {
      const date = new Date(dateString);
      return date.toLocaleDateString('en-IN', {
        day: '2-digit',
        month: 'short',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      });
    } catch (error) {
      return 'Invalid Date';
    }
  };

  const getScoreColor = (score) => {
    const scoreLower = (score || '').toLowerCase();
    if (scoreLower === 'hot') return 'bg-red-100 text-red-800';
    if (scoreLower === 'warm') return 'bg-yellow-100 text-yellow-800';
    if (scoreLower === 'cold') return 'bg-blue-100 text-blue-800';
    return 'bg-gray-100 text-gray-800';
  };

  const getStatusColor = (status) => {
    // Status is already in frontend format or backend stage
    const normalizedStatus = String(status || 'new').toLowerCase();
    switch (normalizedStatus) {
      case 'new': return 'bg-blue-100 text-blue-800';
      case 'contacted': return 'bg-green-100 text-green-800';
      case 'qualified': return 'bg-purple-100 text-purple-800';
      case 'converted': return 'bg-indigo-100 text-indigo-800';
      case 'disqualified': return 'bg-red-100 text-red-800';
      case 'closed_won': return 'bg-emerald-100 text-emerald-800';
      case 'closed_lost': return 'bg-red-100 text-red-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const getPriorityLabel = (priority) => {
    switch (priority) {
      case 1: return 'High';
      case 2: return 'Medium';
      case 3: return 'Low';
      default: return `Priority ${priority}`;
    }
  };

  const getPriorityColor = (priority) => {
    switch (priority) {
      case 1: return 'bg-red-100 text-red-800';
      case 2: return 'bg-yellow-100 text-yellow-800';
      case 3: return 'bg-blue-100 text-blue-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const handleViewDetails = (rule) => {
    const ruleId = rule._id || rule.ruleId;
    navigate(`/assignment_rule_detail/${ruleId}`, { state: { from: location.pathname + location.search } });
  };


  // Notification helper
  const showNotification = (message, type = 'success') => {
    setNotification({ message, type });
    setTimeout(() => setNotification(null), 3000);
  };

  const handleCreateRule = () => {
    setSelectedRule(null);
    setShowRuleModal(true);
  };

  const handleEditRule = async (rule) => {
    // Convert assignees from populated objects to IDs
    const assigneeIds = rule.assignees.map(a =>
      typeof a === 'object' ? a._id || a.id : a
    );

    // Extract user data from populated assignees and add to users array
    const assigneeUserData = rule.assignees
      .filter(a => typeof a === 'object' && (a._id || a.id))
      .map(a => ({
        id: a._id || a.id,
        name: a.name || `${a.firstName || ''} ${a.lastName || ''}`.trim() || a.email || a._id || a.id
          || `${a.firstName || ''} ${a.lastName || ''}`.trim() || a.email || a._id || a.id,
        email: a.email || '',
        department: a.department || '',
        role: a.role || ''
      }));

    // Add assignee user data to users array
    if (assigneeUserData.length > 0) {
      setUsers(prev => {
        const existingMap = new Map(prev.map(u => [u.id, u]));
        assigneeUserData.forEach(u => existingMap.set(u.id, u));
        return Array.from(existingMap.values());
      });
    } else {
      // If assignees are not populated, fetch them by IDs
      await loadUsersByIds(assigneeIds);
    }

    // Set rule with ID-based assignees
    setSelectedRule({ ...rule, assignees: assigneeIds });
    setShowRuleModal(true);
  };

  const handleDeleteRule = (ruleId) => {
    const rule = assignmentRules.find(r => r._id === ruleId || r.ruleId === ruleId);
    if (rule) {
      setRuleToDelete({ id: ruleId, name: rule.name });
      setShowDeleteModal(true);
    }
  };

  const handleBulkDelete = () => {
    if (selectedRows.size === 0) return;
    setShowBulkDeleteModal(true);
  };

  const handleBulkAssignClick = () => {
    if (selectedRows.size > 0) {
      setShowBulkAssignModal(true);
      // Load users for the bulk assign dropdown
      loadUsers('', 'bulk');
    }
  };

  const handleConfirmBulkAssign = async () => {
    if (!bulkAssignee) {
      toast.error('Please select a user to assign leads to');
      return;
    }

    setIsAssigningBulk(true);
    try {
      const leadIds = Array.from(selectedRows);
      const result = await leadService.bulkAssignLeads(leadIds, bulkAssignee);

      if (result.success) {
        toast.success(result.message || `Successfully assigned ${leadIds.length} leads`);
        setSelectedRows(new Set());
        setShowBulkAssignModal(false);
        setBulkAssignee(null);
        // Refresh leads list
        fetchUnassignedLeads();
      } else {
        toast.error(result.error || 'Failed to assign leads');
      }
    } catch (err) {

      toast.error(err.message || 'An error occurred during bulk assignment');
    } finally {
      setIsAssigningBulk(false);
    }
  };

  const confirmBulkDelete = async () => {
    setIsBulkDeleting(true);
    try {
      const selectedIds = Array.from(selectedRows);

      if (activeTab === 'unassigned') {
        const result = await leadService.bulkDeleteLeads(selectedIds);
        if (result.success) {
          toast.success(result.message || `Successfully deleted ${selectedIds.length} leads`);
          setSelectedRows(new Set());
          fetchUnassignedLeads();
        } else {
          toast.error(result.error || 'Failed to delete leads');
        }
      } else if (activeTab === 'rules') {
        // Since there's no bulk delete for rules in service, we delete them one by one
        // or we could check if backend supports it. For now, loop.
        let successCount = 0;
        let lastError = null;

        for (const ruleId of selectedIds) {
          const result = await assignmentRuleService.deleteRule(ruleId);
          if (result.success) {
            successCount++;
          } else {
            lastError = result.error;
          }
        }

        if (successCount > 0) {
          toast.success(`Successfully deleted ${successCount} assignment rules`);
          setSelectedRows(new Set());
          fetchRules();
        }

        if (successCount < selectedIds.length) {
          toast.error(lastError || 'Some rules could not be deleted');
        }
      }
      setShowBulkDeleteModal(false);
    } catch (error) {

      toast.error('An error occurred during bulk deletion');
    } finally {
      setIsBulkDeleting(false);
    }
  };

  const confirmDeleteRule = async () => {
    if (!ruleToDelete) return;

    setIsDeleting(true);
    try {
      const result = await assignmentRuleService.deleteRule(ruleToDelete.id);
      if (result.success) {
        toast.success('Rule deleted successfully!');
        fetchRules();
        setShowDeleteModal(false);
        setRuleToDelete(null);
      } else {
        toast.error(result.error || 'Failed to delete rule');
      }
    } catch (error) {

      toast.error('Failed to delete rule');
    } finally {
      setIsDeleting(false);
    }
  };

  const handleToggleRule = async (ruleId) => {
    try {
      const result = await assignmentRuleService.toggleRuleStatus(ruleId);
      if (result.success) {
        toast.success(`Rule ${result.rule.isActive ? 'activated' : 'deactivated'} successfully!`);
        fetchRules();
      } else {
        toast.error(result.error || 'Failed to toggle rule status');
      }
    } catch (error) {

      toast.error('Failed to toggle rule status');
    }
  };

  const handleAssignLead = async (leadId, assigneeId) => {
    if (!assigneeId) {
      toast.error('Please select an assignee');
      return;
    }

    try {
      const result = await assignmentRuleService.manuallyAssignLead(leadId, assigneeId, 'Manually assigned');
      if (result.success) {
        toast.success('Lead assigned successfully!');
        fetchUnassignedLeads(); // Refresh unassigned leads
      } else {
        toast.error(result.error || 'Failed to assign lead');
      }
    } catch (error) {

      toast.error('Failed to assign lead');
    }
  };

  const handleBulkAssign = async () => {
    if (selectedRows.size === 0) {
      toast.error('Please select leads to assign.');
      return;
    }

    try {
      // Run auto-assignment which will assign based on rules
      const result = await assignmentRuleService.runAutoAssignment();
      if (result.success) {
        toast.success(`Auto-assignment completed: ${result.results.assigned} assigned, ${result.results.skipped} skipped`);
        fetchUnassignedLeads();
        setSelectedRows(new Set());
      } else {
        toast.error(result.error || 'Failed to run bulk assignment');
      }
    } catch (error) {

      toast.error('Failed to run bulk assignment');
    }
  };

  // Show confirmation modal for auto assignment
  const handleShowAutoAssignmentModal = () => {
    setShowAutoAssignmentModal(true);
  };

  // Run auto assignment for all unassigned leads (called after confirmation)
  const handleRunAutoAssignment = async () => {
    setShowAutoAssignmentModal(false);
    setIsRunningAutoAssignment(true);

    try {
      const result = await assignmentRuleService.runAutoAssignment();

      if (result.success) {
        const { assigned, skipped, total } = result.results;
        toast.success(
          `Processed ${total} leads, ${assigned} assigned.`,
          6000
        );
        // Reload unassigned leads to show updated results
        await fetchUnassignedLeads();
      } else {
        toast.error(result.error || 'Failed to run auto-assignment. Please try again.');
      }
    } catch (error) {

      toast.error('Failed to run auto-assignment. Please try again.');
    } finally {
      setIsRunningAutoAssignment(false);
    }
  };




  const mapUnassignedLeadsForExport = (leadsToExport) => {
    return leadsToExport.map(lead => ({
      'Lead ID': lead.leadId || lead._id || 'N/A',
      'Name': getLeadName(lead),
      'Company': lead.company || 'N/A',
      'Email': lead.email || 'N/A',
      'Phone': lead.phone || 'N/A',
      'Score': lead.score || 0,
      'Status': mapStageToFrontendStatus(lead.stage || lead.status || 'new'),
      'Project Cost': formatIndianAmount(lead.totalCost || 0),
      'Created At': formatDate(lead.createdAt),
      'Last Updated': formatDate(lead.updatedAt)
    }));
  };

  const mapAssignmentRulesForExport = (rulesToExport) => {
    return rulesToExport.map(rule => ({
      'Rule Name': rule.name || 'N/A',
      'Status': rule.isActive ? 'Active' : 'Inactive',
      'Priority': getPriorityLabel(rule.priority),
      'Assignees': (rule.assignees || []).length,
      'Assignment Type': (rule.assignmentType || '').replace('_', ' ').charAt(0).toUpperCase() + (rule.assignmentType || '').replace('_', ' ').slice(1),
      'Assignee Names': (rule.assignees || []).map(a => {
        if (typeof a === 'string') return a;
        return a.name || `${a.firstName || ''} ${a.lastName || ''}`.trim() || a.email || (a._id || a.id);
      }).filter(Boolean).join(', '),
      'Criteria Count': (rule.criteria || []).length
    }));
  };

  const handleExportClick = () => {
    if (selectedRows.size > 0 && activeTab === 'unassigned') {
      const leadsToExport = unassignedLeads.filter(lead => {
        const id = lead._id || lead.leadId;
        return selectedRows.has(id);
      });
      const exportData = mapUnassignedLeadsForExport(leadsToExport);
      exportToCSV(exportData, 'unassigned_leads', {
        title: 'Unassigned Leads',
        type: 'Selected Leads'
      });
    } else if (selectedRows.size > 0 && activeTab === 'rules') {
      const rulesToExport = assignmentRules.filter(rule => {
        const id = rule._id || rule.ruleId;
        return selectedRows.has(id);
      });
      const exportData = mapAssignmentRulesForExport(rulesToExport);
      exportToCSV(exportData, 'assignment_rules', {
        title: 'Assignment Rules',
        type: 'Selected Rules'
      });
    } else {
      setShowExportModal(true);
    }
  };

  const handleExportCurrentPage = () => {
    if (activeTab === 'unassigned') {
      const exportData = mapUnassignedLeadsForExport(unassignedLeads);
      exportToCSV(exportData, 'unassigned_leads_page', {
        title: 'Unassigned Leads',
        type: 'Current Page'
      });
    } else {
      const exportData = mapAssignmentRulesForExport(assignmentRules);
      exportToCSV(exportData, 'assignment_rules', {
        title: 'Assignment Rules',
        type: 'Current Page'
      });
    }
    setShowExportModal(false);
  };

  const handleExportRange = async (startDate, endDate) => {
    setExportLoading(true);
    try {
      const queryParams = new URLSearchParams();
      queryParams.append("type", `Date Range (${startDate} to ${endDate})`);
      if (startDate) queryParams.append("startDate", startDate);
      if (endDate) queryParams.append("endDate", endDate);

      const endpoint = activeTab === 'unassigned' ? 'leads' : 'assignment-rules';
      if (activeTab === 'unassigned') {
        if (debouncedSearchQuery) queryParams.append('search', debouncedSearchQuery);
        if (statusFilter !== 'all') queryParams.append('stage', mapStatusToBackend(statusFilter));
        queryParams.append('assignedTo', 'unassigned');
      }

      const url = `${process.env.REACT_APP_API_BASE_URL}/crm/${endpoint}/export?${queryParams.toString()}`;
      const filename = generateExportFilename(`${endpoint}_range`);
      await downloadFromBackend(url, filename);

      setShowExportModal(false);
      toast.success('Export started successfully');
    } catch (error) {

      toast.error("An error occurred during export");
    } finally {
      setExportLoading(false);
    }
  };

  const handleExportAll = async () => {
    setExportLoading(true);
    try {
      const queryParams = new URLSearchParams();
      queryParams.append("type", "All Data");

      const endpoint = activeTab === 'unassigned' ? 'leads' : 'assignment-rules';
      if (activeTab === 'unassigned') {
        if (debouncedSearchQuery) queryParams.append('search', debouncedSearchQuery);
        if (statusFilter !== 'all') queryParams.append('stage', mapStatusToBackend(statusFilter));
        queryParams.append('assignedTo', 'unassigned');
      }

      const url = `${process.env.REACT_APP_API_BASE_URL}/crm/${endpoint}/export?${queryParams.toString()}`;
      const filename = generateExportFilename(`all_${endpoint}`);
      await downloadFromBackend(url, filename);

      setShowExportModal(false);
      toast.success('All data export started');
    } catch (error) {

      toast.error("An error occurred during export");
    } finally {
      setExportLoading(false);
    }
  };

  const handleSelectAll = (e) => {
    if (e.target.checked) {
      if (activeTab === 'unassigned') {
        const allIds = unassignedLeads.map(lead => lead._id || lead.leadId);
        setSelectedRows(new Set(allIds));
      } else {
        const allIds = assignmentRules.map(rule => rule._id || rule.ruleId);
        setSelectedRows(new Set(allIds));
      }
    } else {
      setSelectedRows(new Set());
    }
  };

  const handleSelectRow = (id) => {
    const newSelected = new Set(selectedRows);
    if (newSelected.has(id)) {
      newSelected.delete(id);
    } else {
      newSelected.add(id);
    }
    setSelectedRows(newSelected);
  };

  return (
    <div className="space-y-6">
      {/* Notification */}
      {notification && (
        <div className={`fixed top-4 right-4 z-50 px-6 py-3 rounded-lg shadow-lg max-w-sm ${notification.type === 'success'
          ? 'bg-green-500 text-white'
          : notification.type === 'error'
            ? 'bg-red-500 text-white'
            : 'bg-blue-500 text-white'
          }`}>
          <div className="flex items-center">
            <div className="flex-1">
              <div className="text-sm font-medium">{notification.message}</div>
            </div>
            <button
              onClick={() => setNotification(null)}
              className="ml-4 text-white hover:text-gray-200"
            >
              <X className="h-4 w-4" />
            </button>
          </div>
        </div>
      )}

      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between space-y-4 sm:space-y-0">
        <div className="flex items-center gap-4">
          <div className="bg-slate-900 p-2.5 rounded-xl shadow-sm border border-slate-800 flex items-center justify-center">
            <UserPlus className="h-6 w-6 text-white" />
          </div>
          <div>
            <h1 className="text-2xl sm:text-3xl font-bold text-slate-800 tracking-tight">Lead Assignment</h1>
            <p className="text-sm sm:text-base text-slate-500 mt-1">Configure assignment rules and manually assign leads.</p>
          </div>
        </div>
        <div className="flex items-center space-x-3">
          {canModify && (
            <div className="flex items-center space-x-3">
              <PermissionGate permission="lead_management" action="read">
                <button
                  className="bg-white border border-slate-300 hover:bg-slate-50 text-slate-700 shadow-sm transition-colors rounded-md px-3 py-1.5 text-[13px] font-medium flex items-center justify-center space-x-1.5 h-8"
                  onClick={handleExportClick}
                >
                  <Download className="h-4 w-4" />
                  <span>{selectedRows.size > 0 ? `Export (${selectedRows.size})` : 'Export'}</span>
                </button>
              </PermissionGate>
              {selectedRows.size > 0 && (
                <button
                  className="flex items-center justify-center space-x-1.5 !bg-red-600 hover:!bg-red-700 !text-white rounded-md px-3 py-1.5 text-[13px] font-medium h-8 shadow-sm transition-colors"
                  onClick={handleBulkDelete}
                  disabled={isBulkDeleting}
                >
                  {isBulkDeleting ? <RefreshCw className="h-4 w-4 animate-spin" /> : <Trash2 className="h-4 w-4" />}
                  <span>{isBulkDeleting ? 'Deleting...' : `Delete (${selectedRows.size})`}</span>
                </button>
              )}
              {atLeastOneUnassignedSelected && (
                <PermissionGate permission="lead_management" action="update">
                  <button
                    className="bg-slate-900 hover:bg-slate-800 text-white shadow-sm transition-colors rounded-md px-3 py-1.5 text-[13px] font-medium flex items-center justify-center space-x-1.5 h-8"
                    onClick={handleBulkAssignClick}
                  >
                    <UserPlus className="h-4 w-4" />
                    <span>Assign ({selectedRows.size})</span>
                  </button>
                </PermissionGate>
              )}
              <button
                className="bg-slate-900 hover:bg-slate-800 text-white shadow-sm transition-colors rounded-md px-3 py-1.5 text-[13px] font-medium flex items-center justify-center space-x-1.5 h-8"
                onClick={handleCreateRule}
              >
                <Plus className="h-4 w-4" />
                <span>Create Rule</span>
              </button>
            </div>
          )}
          {activeTab === 'unassigned' && canModify && (
            <button
              className="bg-slate-900 hover:bg-slate-800 text-white shadow-sm transition-colors rounded-md px-3 py-1.5 text-[13px] font-medium flex items-center justify-center space-x-1.5 h-8"
              onClick={handleShowAutoAssignmentModal}
              disabled={isRunningAutoAssignment}
            >
              {isRunningAutoAssignment ? <RefreshCw className="h-4 w-4 animate-spin" /> : <UserPlus className="h-4 w-4" />}
              <span>{isRunningAutoAssignment ? 'Running...' : 'Run Auto Assignment'}</span>
            </button>
          )}
        </div>
      </div>

      {/* Tabs */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200">
        <div className="border-b border-gray-200">
          <nav className="-mb-px flex space-x-8 px-6">
            {[
              { key: 'unassigned', label: 'Unassigned Leads', count: totalUnassignedCount },
              ...(canManageRules ? [{ key: 'rules', label: 'Assignment Rules', count: assignmentRules.length }] : [])
            ].map((tab) => (
              <button
                key={tab.key}
                onClick={() => setActiveTab(tab.key)}
                className={`py-4 px-1 border-b-2 font-medium text-sm ${activeTab === tab.key
                  ? 'border-brand text-brand'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-brand/30'
                  }`}
              >
                {tab.label}
                <span className={`ml-2 py-0.5 px-2 rounded-full text-xs ${activeTab === tab.key
                  ? 'bg-brand text-white'
                  : 'bg-gray-100 text-gray-600'
                  }`}>
                  {tab.count}
                </span>
              </button>
            ))}
          </nav>
        </div>

        <div className="p-6">
          {activeTab === 'rules' && (
            <div className="space-y-6">
              <div className="flex flex-col space-y-4">
                <div className="flex items-center justify-between">
                  <h3 className="text-lg font-semibold text-gray-900">Assignment Rules</h3>
                  <div className="flex items-center space-x-2 bg-white rounded-lg border border-gray-200 p-2">
                    <DateRangeFilter
                      startDate={rulesDateRange.startDate}
                      endDate={rulesDateRange.endDate}
                      onChange={setRulesDateRange}
                    />
                  </div>
                </div>

                {/* Rules Search and Filter Bar */}
                <div className="bg-white rounded-lg border border-gray-200 p-4">
                  <div className="flex flex-col sm:flex-row gap-3">
                    {/* Search Bar */}
                    <div className="flex-1 relative">
                      <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
                      <input
                        type="text"
                        value={ruleSearchQuery}
                        onChange={(e) => {
                          const sanitized = handleSearchInputChange(e, null, FIELD_LIMITS.search || 200);
                          setRuleSearchQuery(sanitized);
                        }}
                        placeholder="Search by rule name, assignee name or email..."
                        className="input-field pl-10 pr-4"
                        maxLength={FIELD_LIMITS.search || 200}
                      />
                    </div>

                    {/* Status Filter */}
                    <div className="flex-shrink-0">
                      <select
                        value={ruleStatusFilter}
                        onChange={(e) => setRuleStatusFilter(e.target.value)}
                        className="input-field w-full sm:w-auto"
                      >
                        <option value="all">All Status</option>
                        <option value="active">Active</option>
                        <option value="inactive">Inactive</option>
                      </select>
                    </div>

                    {/* Priority Filter */}
                    <div className="flex-shrink-0">
                      <select
                        value={rulePriorityFilter}
                        onChange={(e) => setRulePriorityFilter(e.target.value)}
                        className="input-field w-full sm:w-auto"
                      >
                        <option value="all">All Priorities</option>
                        <option value="1">High Priority</option>
                        <option value="2">Medium Priority</option>
                        <option value="3">Low Priority</option>
                      </select>
                    </div>

                    {/* Assignment Type Filter */}
                    <div className="flex-shrink-0">
                      <select
                        value={ruleAssignmentTypeFilter}
                        onChange={(e) => setRuleAssignmentTypeFilter(e.target.value)}
                        className="input-field w-full sm:w-auto"
                      >
                        <option value="all">All Assignment Types</option>
                        <option value="round_robin">Round Robin</option>
                        <option value="manual">Manual Assignment</option>
                      </select>
                    </div>

                    {/* Clear Filters Button */}
                    {(ruleSearchQuery || ruleStatusFilter !== 'all' || rulePriorityFilter !== 'all' || ruleAssignmentTypeFilter !== 'all' || rulesDateRange.startDate || rulesDateRange.endDate) && (
                      <button
                        onClick={handleClearFilters}
                        className="btn-cancel flex items-center justify-center whitespace-nowrap"
                      >
                        <RefreshCw className="w-4 h-4 mr-2" />
                        Clear
                      </button>
                    )}
                  </div>
                </div>
              </div>

              {loading ? (
                <div className="flex items-center justify-center py-12">
                  <div className="text-center">
                    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-brand mx-auto mb-4"></div>
                    <p className="text-gray-600">Loading assignment rules...</p>
                  </div>
                </div>
              ) : assignmentRules.length === 0 ? (
                <div className="text-center py-12">
                  <p className="text-gray-500">No assignment rules found. Create your first rule to get started.</p>
                </div>
              ) : (
                <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
                  <div className="overflow-x-auto">
                    <table className="w-full divide-y divide-gray-200">
                      <thead className="bg-gray-50">
                        <tr>
                          <th className="px-6 py-3 text-left w-12 checkbox-column">
                            <input
                              type="checkbox"
                              className="h-4 w-4 text-brand border-gray-300 rounded focus:ring-brand"
                              checked={assignmentRules.length > 0 && selectedRows.size === assignmentRules.length}
                              onChange={handleSelectAll}
                            />
                          </th>
                          <th
                            scope="col"
                            className="px-6 py-3 text-left text-xs font-medium text-gray-500 tracking-wider w-[250px] cursor-pointer hover:bg-gray-100 select-none"
                            onClick={() => handleRulesSort('name')}
                          >
                            <div className="flex items-center space-x-1">
                              <span>Rule Name</span>
                              {rulesSortColumn === 'name' ? (
                                rulesSortOrder === 'asc' ? <ArrowUp className="h-4 w-4" /> : <ArrowDown className="h-4 w-4" />
                              ) : (
                                <ArrowUpDown className="h-4 w-4 text-gray-400" />
                              )}
                            </div>
                          </th>
                          <th
                            scope="col"
                            className="px-6 py-3 text-left text-xs font-medium text-gray-500 tracking-wider whitespace-nowrap w-[120px] cursor-pointer hover:bg-gray-100 select-none"
                            onClick={() => handleRulesSort('isActive')}
                          >
                            <div className="flex items-center space-x-1">
                              <span>Status</span>
                              {rulesSortColumn === 'isActive' ? (
                                rulesSortOrder === 'asc' ? <ArrowUp className="h-4 w-4" /> : <ArrowDown className="h-4 w-4" />
                              ) : (
                                <ArrowUpDown className="h-4 w-4 text-gray-400" />
                              )}
                            </div>
                          </th>
                          <th
                            scope="col"
                            className="px-6 py-3 text-left text-xs font-medium text-gray-500 tracking-wider whitespace-nowrap w-[120px] cursor-pointer hover:bg-gray-100 select-none"
                            onClick={() => handleRulesSort('priority')}
                          >
                            <div className="flex items-center space-x-1">
                              <span>Priority</span>
                              {rulesSortColumn === 'priority' ? (
                                rulesSortOrder === 'asc' ? <ArrowUp className="h-4 w-4" /> : <ArrowDown className="h-4 w-4" />
                              ) : (
                                <ArrowUpDown className="h-4 w-4 text-gray-400" />
                              )}
                            </div>
                          </th>
                          <th
                            scope="col"
                            className="px-6 py-3 text-left text-xs font-medium text-gray-500 tracking-wider whitespace-nowrap w-[100px] cursor-pointer hover:bg-gray-100 select-none"
                            onClick={() => handleRulesSort('assignees')}
                          >
                            <div className="flex items-center space-x-1">
                              <span>Assignees</span>
                              {rulesSortColumn === 'assignees' ? (
                                rulesSortOrder === 'asc' ? <ArrowUp className="h-4 w-4" /> : <ArrowDown className="h-4 w-4" />
                              ) : (
                                <ArrowUpDown className="h-4 w-4 text-gray-400" />
                              )}
                            </div>
                          </th>
                          <th
                            scope="col"
                            className="px-6 py-3 text-left text-xs font-medium text-gray-500 tracking-wider whitespace-nowrap w-[150px] cursor-pointer hover:bg-gray-100 select-none"
                            onClick={() => handleRulesSort('assignmentType')}
                          >
                            <div className="flex items-center space-x-1">
                              <span>Assignment Type</span>
                              {rulesSortColumn === 'assignmentType' ? (
                                rulesSortOrder === 'asc' ? <ArrowUp className="h-4 w-4" /> : <ArrowDown className="h-4 w-4" />
                              ) : (
                                <ArrowUpDown className="h-4 w-4 text-gray-400" />
                              )}
                            </div>
                          </th>
                          <th
                            scope="col"
                            className="px-6 py-3 text-left text-xs font-medium text-gray-500 tracking-wider whitespace-nowrap w-[150px] cursor-pointer hover:bg-gray-100 select-none"
                            onClick={() => handleRulesSort('createdAt')}
                          >
                            <div className="flex items-center space-x-1">
                              <span>Created At</span>
                              {rulesSortColumn === 'createdAt' ? (
                                rulesSortOrder === 'asc' ? <ArrowUp className="h-4 w-4" /> : <ArrowDown className="h-4 w-4" />
                              ) : (
                                <ArrowUpDown className="h-4 w-4 text-gray-400" />
                              )}
                            </div>
                          </th>
                          <th
                            scope="col"
                            className="px-6 py-3 text-left text-xs font-medium text-gray-500 tracking-wider whitespace-nowrap w-[150px] cursor-pointer hover:bg-gray-100 select-none"
                            onClick={() => handleRulesSort('updatedAt')}
                          >
                            <div className="flex items-center space-x-1">
                              <span>Updated At</span>
                              {rulesSortColumn === 'updatedAt' ? (
                                rulesSortOrder === 'asc' ? <ArrowUp className="h-4 w-4" /> : <ArrowDown className="h-4 w-4" />
                              ) : (
                                <ArrowUpDown className="h-4 w-4 text-gray-400" />
                              )}
                            </div>
                          </th>
                          <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 tracking-wider whitespace-nowrap w-[180px]">
                            Actions
                          </th>
                        </tr>
                      </thead>
                      <tbody className="bg-white divide-y divide-gray-200">
                        {getSortedAssignmentRules().map((rule) => {
                          const assigneeCount = rule.assignees ? rule.assignees.length : 0;
                          const ruleId = rule._id || rule.ruleId;
                          const isSelected = selectedRows.has(ruleId);
                          return (
                            <tr key={ruleId} className={`hover:bg-gray-50 ${isSelected ? 'bg-blue-50' : ''}`}>
                              <td className="px-6 py-4 whitespace-nowrap checkbox-cell">
                                <input
                                  type="checkbox"
                                  className="h-4 w-4 text-brand border-gray-300 rounded focus:ring-brand"
                                  checked={isSelected}
                                  onChange={() => handleSelectRow(ruleId)}
                                />
                              </td>
                              <td className="px-6 py-4 max-w-[250px]">
                                <div className="text-sm font-medium text-gray-900 truncate min-w-0 first-letter:capitalize" title={rule.name}>
                                  {rule.name}
                                </div>
                              </td>
                              <td className="px-6 py-4 whitespace-nowrap">
                                <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium capitalize ${rule.isActive ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-800'
                                  }`}>
                                  {rule.isActive ? 'Active' : 'Inactive'}
                                </span>
                              </td>
                              <td className="px-6 py-4 whitespace-nowrap">
                                <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium capitalize ${getPriorityColor(rule.priority)}`}>
                                  {getPriorityLabel(rule.priority)}
                                </span>
                              </td>
                              <td className="px-6 py-4 whitespace-nowrap">
                                <div className="flex items-center space-x-1">
                                  <Users className="h-4 w-4 text-gray-400" />
                                  <span className="text-sm text-gray-900 font-medium">{assigneeCount}</span>
                                </div>
                              </td>
                              <td className="px-6 py-4 whitespace-nowrap">
                                <span className="text-sm text-gray-900 capitalize">{rule.assignmentType.replace('_', ' ')}</span>
                              </td>
                              <td className="px-6 py-4 whitespace-nowrap">
                                <span className="text-sm text-gray-500">{formatDate(rule.createdAt)}</span>
                              </td>
                              <td className="px-6 py-4 whitespace-nowrap">
                                <span className="text-sm text-gray-500">{formatDate(rule.updatedAt || rule.createdAt)}</span>
                              </td>
                              <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                                <div className="flex items-center space-x-2">
                                  <button
                                    onClick={() => handleViewDetails(rule)}
                                    className="text-green-600 hover:text-green-900"
                                    title="View Details"
                                  >
                                    <Eye className="h-4 w-4" />
                                  </button>
                                  {canModify && (
                                    <>
                                      <button
                                        onClick={() => handleEditRule(rule)}
                                        className="text-[#0F2942] hover:text-blue-900"
                                        title="Edit Rule"
                                      >
                                        <Edit className="h-4 w-4" />
                                      </button>
                                      <button
                                        onClick={() => handleToggleRule(rule._id || rule.ruleId)}
                                        className={rule.isActive
                                          ? "text-orange-600 hover:text-orange-900"
                                          : "text-green-600 hover:text-green-900"
                                        }
                                        title={rule.isActive ? 'Deactivate Rule' : 'Activate Rule'}
                                      >
                                        {rule.isActive ? <X className="h-4 w-4" /> : <CheckCircle className="h-4 w-4" />}
                                      </button>
                                      <button
                                        onClick={() => handleDeleteRule(rule._id || rule.ruleId)}
                                        className="text-red-600 hover:text-red-900"
                                        title="Delete Rule"
                                      >
                                        <Trash2 className="h-4 w-4" />
                                      </button>
                                    </>
                                  )}
                                </div>
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}
            </div>
          )}

          {activeTab === 'unassigned' && (
            <div className="space-y-6">

              {/* Search and Filter Bar */}
              <div className="bg-white rounded-lg border border-gray-200 p-4">
                <div className="flex flex-col sm:flex-row gap-3">
                  {/* Search Bar */}
                  <div className="flex-1 relative">
                    <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
                    <input
                      type="text"
                      value={searchQuery}
                      onChange={(e) => {
                        const sanitized = handleSearchInputChange(e, null, FIELD_LIMITS.search || 200);
                        setSearchQuery(sanitized);
                      }}
                      placeholder="Search by Lead ID, name, email, company, phone..."
                      className="input-field pl-10 pr-4"
                      maxLength={FIELD_LIMITS.search || 200}
                    />
                  </div>

                  {/* Status Filter */}
                  <div className="flex-shrink-0">
                    <select
                      value={statusFilter}
                      onChange={(e) => setStatusFilter(e.target.value)}
                      className="input-field w-full sm:w-48"
                    >
                      <option value="all">All Status</option>
                      {masterLeadStatuses.length > 0 &&
                        masterLeadStatuses.map(s => (
                          <option key={s._id} value={s.value}>{s.name}</option>
                        ))
                      }
                    </select>
                  </div>

                  {/* Date Filter */}
                  <div className="flex-shrink-0">
                    <DateRangeFilter
                      startDate={unassignedDateRange.startDate}
                      endDate={unassignedDateRange.endDate}
                      onChange={setUnassignedDateRange}
                    />
                  </div>

                  {/* Clear Button */}
                  {hasActiveFilters && (
                    <Button
                      variant="outline"
                      icon={<X className="h-4 w-4" />}
                      onClick={handleClearFilters}
                      className="btn-cancel flex-shrink-0 whitespace-nowrap text-white hover:text-white"
                    >
                      Clear
                    </Button>
                  )}
                </div>
              </div>

              {loadingLeads ? (
                <div className="flex items-center justify-center py-12">
                  <div className="text-center">
                    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-brand mx-auto mb-4"></div>
                    <p className="text-gray-600">Loading unassigned leads...</p>
                  </div>
                </div>
              ) : unassignedLeads.length === 0 ? (
                <div className="text-center py-12">
                  <p className="text-gray-500">No unassigned leads found.</p>
                </div>
              ) : (
                <div className="relative">
                  <div className="overflow-x-auto">
                    <table className="min-w-full divide-y divide-gray-200">
                      <thead className="bg-gray-50">
                        <tr>
                          <th className="px-4 py-3 text-left w-12">
                            <input
                              type="checkbox"
                              className="h-4 w-4 text-brand border-gray-300 rounded focus:ring-brand"
                              checked={unassignedLeads.length > 0 && selectedRows.size === unassignedLeads.length}
                              onChange={handleSelectAll}
                            />
                          </th>
                          <th
                            className="px-4 py-3 text-left text-xs font-medium text-gray-500 tracking-wider w-1/12 cursor-pointer hover:bg-gray-100 select-none"
                            onClick={() => handleUnassignedSort('leadId')}
                          >
                            <div className="flex items-center space-x-1">
                              <span>Lead ID</span>
                              {unassignedSortColumn === 'leadId' ? (
                                unassignedSortOrder === 'asc' ? <ArrowUp className="h-4 w-4" /> : <ArrowDown className="h-4 w-4" />
                              ) : (
                                <ArrowUpDown className="h-4 w-4 text-gray-400" />
                              )}
                            </div>
                          </th>
                          <th
                            className="px-4 py-3 text-left text-xs font-medium text-gray-500 tracking-wider w-3/12 cursor-pointer hover:bg-gray-100 select-none"
                            onClick={() => handleUnassignedSort('details')}
                          >
                            <div className="flex items-center space-x-1">
                              <span>Lead Details</span>
                              {unassignedSortColumn === 'details' ? (
                                unassignedSortOrder === 'asc' ? <ArrowUp className="h-4 w-4" /> : <ArrowDown className="h-4 w-4" />
                              ) : (
                                <ArrowUpDown className="h-4 w-4 text-gray-400" />
                              )}
                            </div>
                          </th>
                          <th
                            className="px-4 py-3 text-left text-xs font-medium text-gray-500 tracking-wider w-1/12 cursor-pointer hover:bg-gray-100 select-none"
                            onClick={() => handleUnassignedSort('score')}
                          >
                            <div className="flex items-center space-x-1">
                              <span>Score</span>
                              {unassignedSortColumn === 'score' ? (
                                unassignedSortOrder === 'asc' ? <ArrowUp className="h-4 w-4" /> : <ArrowDown className="h-4 w-4" />
                              ) : (
                                <ArrowUpDown className="h-4 w-4 text-gray-400" />
                              )}
                            </div>
                          </th>
                          <th
                            className="px-4 py-3 text-left text-xs font-medium text-gray-500 tracking-wider w-1/12 cursor-pointer hover:bg-gray-100 select-none"
                            onClick={() => handleUnassignedSort('status')}
                          >
                            <div className="flex items-center space-x-1">
                              <span>Status</span>
                              {unassignedSortColumn === 'status' ? (
                                unassignedSortOrder === 'asc' ? <ArrowUp className="h-4 w-4" /> : <ArrowDown className="h-4 w-4" />
                              ) : (
                                <ArrowUpDown className="h-4 w-4 text-gray-400" />
                              )}
                            </div>
                          </th>
                          <th
                            className="px-4 py-3 text-left text-xs font-medium text-gray-500 tracking-wider w-1/12 cursor-pointer hover:bg-gray-100 select-none"
                            onClick={() => handleUnassignedSort('projectCost')}
                          >
                            <div className="flex items-center space-x-1">
                              <span>Project Cost</span>
                              {unassignedSortColumn === 'projectCost' ? (
                                unassignedSortOrder === 'asc' ? <ArrowUp className="h-4 w-4" /> : <ArrowDown className="h-4 w-4" />
                              ) : (
                                <ArrowUpDown className="h-4 w-4 text-gray-400" />
                              )}
                            </div>
                          </th>
                          <th
                            className="px-4 py-3 text-left text-xs font-medium text-gray-500 tracking-wider w-2/12 cursor-pointer hover:bg-gray-100 select-none"
                            onClick={() => handleUnassignedSort('createdAt')}
                          >
                            <div className="flex items-center space-x-1">
                              <span>Created</span>
                              {unassignedSortColumn === 'createdAt' ? (
                                unassignedSortOrder === 'asc' ? <ArrowUp className="h-4 w-4" /> : <ArrowDown className="h-4 w-4" />
                              ) : (
                                <ArrowUpDown className="h-4 w-4 text-gray-400" />
                              )}
                            </div>
                          </th>
                          <th
                            className="px-4 py-3 text-left text-xs font-medium text-gray-500 tracking-wider w-2/12 cursor-pointer hover:bg-gray-100 select-none"
                            onClick={() => handleUnassignedSort('updatedAt')}
                          >
                            <div className="flex items-center space-x-1">
                              <span>Last Activity</span>
                              {unassignedSortColumn === 'updatedAt' ? (
                                unassignedSortOrder === 'asc' ? <ArrowUp className="h-4 w-4" /> : <ArrowDown className="h-4 w-4" />
                              ) : (
                                <ArrowUpDown className="h-4 w-4 text-gray-400" />
                              )}
                            </div>
                          </th>
                          <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 tracking-wider w-2/12">
                            Actions
                          </th>
                        </tr>
                      </thead>
                      <tbody className="bg-white divide-y divide-gray-200">
                        {getSortedUnassignedLeads().map((lead) => {
                          const leadId = lead._id || lead.leadId;
                          const isSelected = selectedRows.has(leadId);
                          const leadName = getLeadName(lead);
                          const projectCost = lead.totalCost || 0;
                          const scoreLabel = (lead.scoreLabel || (lead.score >= 70 ? 'hot' : lead.score >= 40 ? 'warm' : 'cold')).toLowerCase();
                          // Map backend stage to frontend status display
                          const status = mapStageToFrontendStatus(lead.stage || lead.status || 'new');

                          return (
                            <tr key={leadId} className={`hover:bg-gray-50 ${isSelected ? 'bg-blue-50' : ''}`}>
                              <td className="px-4 py-4 whitespace-nowrap">
                                <input
                                  type="checkbox"
                                  className="h-4 w-4 text-brand border-gray-300 rounded focus:ring-brand"
                                  checked={isSelected}
                                  onChange={() => handleSelectRow(leadId)}
                                />
                              </td>
                              <td className="px-4 py-4 whitespace-nowrap">
                                <span className="text-xs text-gray-900 font-medium font-mono" title={lead.leadId || 'No ID'}>{lead.leadId || 'No ID'}</span>
                              </td>
                              <td className="px-4 py-4 min-w-[200px] max-w-[300px]">
                                <div className="flex items-center space-x-3 min-w-0">
                                  <div className="min-w-0 flex-1 overflow-hidden">
                                    <div
                                      className="text-sm font-medium text-gray-900 truncate capitalize cursor-pointer hover:text-[#0F2942]"
                                      title={leadName}
                                      onClick={() => handleViewLead(leadId)}
                                    >
                                      {leadName}
                                    </div>
                                    <div className="text-sm text-gray-500 truncate first-letter:capitalize" title={lead.company || 'No company'}>{lead.company || 'No company'}</div>
                                    <div className="text-xs text-gray-400 truncate" title={lead.email || 'No email'}>{lead.email || 'No email'}</div>
                                  </div>
                                </div>
                              </td>
                              <td className="px-4 py-4 whitespace-nowrap">
                                <span className="text-sm font-medium text-gray-900">{lead.score || 0}</span>                        </td>                              <td className="px-4 py-4 whitespace-nowrap">
                                <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getStatusColor(status)}`}>
                                  {status}
                                </span>
                              </td>
                              <td className="px-4 py-4 whitespace-nowrap">
                                <span className="text-sm font-medium text-gray-900">₹{projectCost ? formatIndianAmount(projectCost) : '0'}</span>
                              </td>
                              <td className="px-4 py-4 whitespace-nowrap">
                                <span className="text-sm text-gray-500">{formatDate(lead.createdAt)}</span>
                              </td>
                              <td className="px-4 py-4 whitespace-nowrap">
                                <span className="text-sm text-gray-500">{formatDate(lead.updatedAt || lead.createdAt)}</span>
                              </td>
                              <td className="px-4 py-4 whitespace-nowrap text-sm font-medium" style={{ position: 'relative', overflow: 'visible' }}>
                                <div className="relative w-full" ref={el => leadDropdownRefs.current[leadId] = el}>
                                  <button
                                    type="button"
                                    onClick={() => {
                                      const isOpening = openDropdownLeadId !== leadId;
                                      setOpenDropdownLeadId(isOpening ? leadId : null);
                                      if (isOpening && !leadAllUsers[leadId]) {
                                        loadUsersForLead(leadId, leadSearchTerms[leadId] || '');
                                      }
                                    }}
                                    disabled={loadingUsers}
                                    className="input-field px-3 py-1.5 flex items-center justify-between text-left bg-white disabled:bg-gray-100 disabled:cursor-not-allowed"
                                  >
                                    <span className="truncate text-sm">Select Assignee</span>
                                    <ChevronDown className={`w-4 h-4 text-gray-400 flex-shrink-0 ml-2 transition-transform ${openDropdownLeadId === leadId ? 'rotate-180' : ''}`} />
                                  </button>

                                  {/* Dropdown */}
                                  {openDropdownLeadId === leadId && dropdownPositions[leadId] && (
                                    <div
                                      data-dropdown-lead-id={leadId}
                                      className="fixed z-[9999] bg-white border border-gray-200 rounded-lg shadow-xl"
                                      style={{
                                        top: `${dropdownPositions[leadId].top}px`,
                                        left: `${dropdownPositions[leadId].left}px`,
                                        minWidth: '200px',
                                        maxWidth: '300px',
                                        width: `${Math.max(200, dropdownPositions[leadId].width)}px`
                                      }}
                                    >
                                      <div className="p-2 border-b border-gray-100">
                                        <div className="relative">
                                          <Search className="absolute left-2 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
                                          <input
                                            type="text"
                                            value={leadSearchTerms[leadId] || ''}
                                            onChange={(e) => {
                                              const value = e.target.value.replace(/[^a-zA-Z0-9\s@._+-]/g, '').slice(0, 100);
                                              setLeadSearchTerms(prev => ({
                                                ...prev,
                                                [leadId]: value
                                              }));
                                            }}
                                            placeholder="Search users..."
                                            maxLength={100}
                                            className="input-field text-sm"
                                            autoFocus
                                          />
                                        </div>
                                      </div>
                                      <div className="overflow-y-auto max-h-[240px] overflow-x-hidden">
                                        {loadingUsers && (!leadFilteredUsers[leadId] || leadFilteredUsers[leadId].length === 0) ? (
                                          <div className="p-3 text-sm text-gray-500 text-center">
                                            Loading users...
                                          </div>
                                        ) : (!leadFilteredUsers[leadId] || leadFilteredUsers[leadId].length === 0) ? (
                                          <div className="p-3 text-sm text-gray-500 text-center">
                                            {leadSearchTerms[leadId] ? 'No users found' : 'No users available'}
                                          </div>
                                        ) : (
                                          <div className="flex flex-col">
                                            {leadFilteredUsers[leadId].map(user => (
                                              <button
                                                key={user.id}
                                                type="button"
                                                onClick={() => {
                                                  handleAssignLead(leadId, user.id);
                                                  setOpenDropdownLeadId(null);
                                                  setLeadSearchTerms(prev => ({
                                                    ...prev,
                                                    [leadId]: ''
                                                  }));
                                                }}
                                                className="w-full text-left px-3 py-2 text-sm hover:bg-gray-50 border-b border-gray-100 last:border-b-0 text-gray-700 flex flex-col"
                                              >
                                                <div className="truncate font-medium break-words">{user.name}</div>
                                                {user.email && (
                                                  <div className="text-xs text-gray-500 truncate break-words">{user.email}</div>
                                                )}
                                                {user.designation && (
                                                  <div className="text-xs text-gray-500 truncate capitalize break-words">{user.designation}</div>
                                                )}
                                              </button>
                                            ))}
                                          </div>
                                        )}
                                      </div>
                                    </div>
                                  )}
                                </div>
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>

                  {/* Pagination for Unassigned Leads */}
                  {(() => {
                    const leadsTotalItems = Number(unassignedPagination?.totalLeads || unassignedLeads.length || 0);
                    const leadsTotalPages = Number(unassignedPagination?.totalPages || 1);
                    const effectivePaginationLimit = unassignedPagination?.limit || itemsPerPage || 10;
                    // Show pagination if there's more than one page
                    const shouldShowPagination = !loadingLeads && leadsTotalPages > 1;
                    const handleUnassignedPageChange = (page) => {
                      setCurrentPage(page);
                      window.scrollTo({ top: 0, behavior: 'smooth' });
                    };

                    return shouldShowPagination ? (
                      <PaginationControls
                        currentPage={unassignedPagination.currentPage || currentPage || 1}
                        totalPages={leadsTotalPages}
                        totalItems={leadsTotalItems}
                        limit={effectivePaginationLimit}
                        hasNextPage={unassignedPagination.hasNextPage !== false && (unassignedPagination.currentPage || 1) < leadsTotalPages}
                        hasPrevPage={unassignedPagination.hasPrevPage !== false && (unassignedPagination.currentPage || 1) > 1}
                        onPageChange={handleUnassignedPageChange}
                        onLimitChange={(limit) => handleUnassignedItemsPerPageChange(limit)}
                        limitOptions={[3, 10, 25, 50, 100]}
                      />
                    ) : null;
                  })()}
                </div>
              )}
            </div>
          )}

        </div>
      </div>

      {/* Rule Modal */}
      {showRuleModal && (
        <AssignmentRuleModal
          isOpen={showRuleModal}
          onClose={() => setShowRuleModal(false)}
          ruleToEdit={selectedRule}
          users={users}
          onSearchUsers={loadUsers}
          loadingUsers={loadingUsers}
          filteredUsers={filteredUsers}
          canModify={canModify}
          onSuccess={fetchRules}
        />
      )}

      {/* Delete Confirmation Modal */}
      {showDeleteModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 !mt-0">
          <div className="bg-white rounded-lg p-6 w-full max-w-md mx-4">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold text-gray-900">Delete Assignment Rule</h3>
              <button
                onClick={() => {
                  setShowDeleteModal(false);
                  setRuleToDelete(null);
                }}
                className="text-gray-400 hover:text-gray-600"
                disabled={isDeleting}
              >
                <X className="h-5 w-5" />
              </button>
            </div>

            <div className="mb-6">
              <p className="text-sm text-gray-600 break-words">
                Are you sure you want to delete the rule: <span className="font-medium text-gray-900 first-letter:capitalize">"{ruleToDelete?.name}"</span>? This action cannot be undone.
              </p>
            </div>

            <div className="flex items-center justify-end space-x-3">
              <Button
                variant="outline"
                onClick={() => {
                  setShowDeleteModal(false);
                  setRuleToDelete(null);
                }}
                disabled={isDeleting}
              >
                Cancel
              </Button>
              {canModify && (
                <Button
                  variant="primary"
                  icon={isDeleting ? <RefreshCw className="h-4 w-4 animate-spin" /> : <Trash2 className="h-4 w-4" />}
                  className="bg-red-600 hover:bg-red-700"
                  onClick={confirmDeleteRule}
                  disabled={isDeleting}
                >
                  {isDeleting ? 'Deleting...' : 'Delete'}
                </Button>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Bulk Delete Confirmation Modal */}
      {showBulkDeleteModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[100] !mt-0">
          <div className="bg-white rounded-lg p-6 w-full max-w-lg mx-4">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold text-gray-900 flex items-center">
                <AlertCircle className="w-5 h-5 text-red-600 mr-2" />
                Bulk Delete {activeTab === 'unassigned' ? 'Leads' : 'Assignment Rules'}
              </h3>
              <button
                onClick={() => !isBulkDeleting && setShowBulkDeleteModal(false)}
                className="text-gray-400 hover:text-gray-600"
                disabled={isBulkDeleting}
              >
                <X className="h-5 w-5" />
              </button>
            </div>

            <div className="mb-6">
              <p className="text-sm text-gray-600">
                Are you sure you want to delete <span className="font-bold text-gray-900">{selectedRows.size}</span> selected {activeTab === 'unassigned' ? 'leads' : 'assignment rules'}? This action cannot be undone.
              </p>
            </div>

            <div className="flex items-center justify-end space-x-3">
              <Button
                className="btn-cancel"
                onClick={() => setShowBulkDeleteModal(false)}
                disabled={isBulkDeleting}
              >
                Cancel
              </Button>
              <Button
                variant="primary"
                icon={isBulkDeleting ? <RefreshCw className="h-4 w-4 animate-spin" /> : <Trash2 className="h-4 w-4" />}
                className="bg-red-600 hover:bg-red-700"
                onClick={confirmBulkDelete}
                disabled={isBulkDeleting}
              >
                {isBulkDeleting ? 'Deleting...' : `Delete All Selected (${selectedRows.size})`}
              </Button>
            </div>
          </div>
        </div>
      )}


      {/* Bulk Assign Modal */}
      {showBulkAssignModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[100] !mt-0">
          <div className="bg-white rounded-lg shadow-lg p-6 max-w-md w-full mx-4">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold text-gray-900">Bulk Assign Leads</h3>
              <button
                onClick={() => !isAssigningBulk && setShowBulkAssignModal(false)}
                className="text-gray-400 hover:text-gray-600"
                disabled={isAssigningBulk}
              >
                <X className="h-5 w-5" />
              </button>
            </div>

            <div className="mb-6">
              <p className="text-sm text-gray-600 mb-4">
                Select a user to assign <strong>{selectedRows.size}</strong> leads to:
              </p>

              <div className="relative">
                <div className="flex items-center bg-gray-50 border border-gray-300 rounded px-3 py-2">
                  <Search className="h-4 w-4 text-gray-400 mr-2" />
                  <input
                    type="text"
                    placeholder="Search user..."
                    className="input-field text-sm"
                    value={bulkAssignSearchTerm}
                    onChange={(e) => {
                      setBulkAssignSearchTerm(e.target.value);
                      loadUsers(e.target.value, 'bulk');
                    }}
                  />
                </div>

                <div className="mt-2 max-h-48 overflow-y-auto border border-gray-200 rounded">
                  {loadingUsers ? (
                    <div className="p-2 text-center text-xs text-gray-500">Loading users...</div>
                  ) : bulkAssignUsers.length > 0 ? (
                    bulkAssignUsers.map(user => (
                      <div
                        key={user.id}
                        className={`px-3 py-2 text-sm cursor-pointer hover:bg-gray-100 ${bulkAssignee === user.id ? 'bg-blue-50 text-[#0B2033]' : 'text-gray-700'}`}
                        onClick={() => setBulkAssignee(user.id)}
                      >
                        <div className="font-medium">{user.name}</div>
                        <div className="text-xs text-gray-500">{user.email} - {user.role}</div>
                      </div>
                    ))
                  ) : (
                    <div className="p-2 text-center text-xs text-gray-500">No users found</div>
                  )}
                </div>
              </div>

              {isAssigningBulk && (
                <div className="mt-4 flex items-center justify-center">
                  <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-[#0F2942]"></div>
                  <span className="ml-2 text-sm text-gray-600">Assigning leads...</span>
                </div>
              )}
            </div>

            <div className="flex items-center justify-end space-x-3">
              <Button
                className="btn-cancel"
                onClick={() => setShowBulkAssignModal(false)}
                disabled={isAssigningBulk}
              >
                Cancel
              </Button>
              <PermissionGate permission="lead_management" action="update">
                <Button
                  variant="primary"
                  onClick={handleConfirmBulkAssign}
                  disabled={isAssigningBulk || !bulkAssignee}
                  className="btn-create"
                >
                  {isAssigningBulk ? 'Assigning...' : 'Assign All'}
                </Button>
              </PermissionGate>
            </div>
          </div>
        </div>
      )}

      {/* Auto Assignment Confirmation Modal */}
      {showAutoAssignmentModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 !mt-0">
          <div className="bg-white rounded-lg p-6 w-full max-w-md mx-4">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold text-gray-900">Run Auto Assignment</h3>
              <button
                onClick={() => setShowAutoAssignmentModal(false)}
                className="text-gray-400 hover:text-gray-600"
                disabled={isRunningAutoAssignment}
              >
                <X className="h-5 w-5" />
              </button>
            </div>

            <div className="mb-6">
              <p className="text-sm text-gray-600">
                This will run auto-assignment for all unassigned leads based on your active assignment rules.
                This process may take a while depending on the number of leads.
              </p>
            </div>

            <div className="flex items-center justify-end space-x-3">
              <Button
                className="btn-cancel"
                onClick={() => setShowAutoAssignmentModal(false)}
                disabled={isRunningAutoAssignment}
              >
                Cancel
              </Button>
              <Button
                variant="primary"
                icon={<UserPlus className="h-4 w-4" />}
                onClick={handleRunAutoAssignment}
                disabled={isRunningAutoAssignment}
                className="btn-create"
              >
                {isRunningAutoAssignment ? 'Starting...' : 'Run Assignment'}
              </Button>
            </div>
          </div>
        </div>
      )}
      <ExportModal
        isOpen={showExportModal}
        onClose={() => setShowExportModal(false)}
        onExportCurrentPage={handleExportCurrentPage}
        onExportRange={handleExportRange}
        onExportAll={handleExportAll}
        loading={exportLoading}
        title={activeTab === 'unassigned' ? 'Export Unassigned Leads' : 'Export Assignment Rules'}
      />
    </div>
  );
};

export default LeadAssignment;
